import { AuthGuard, Button, Layout, ToastProvider } from "@adafia/ui";
import { Menu, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BrowserRouter, Route, useNavigate, useParams } from "react-router-dom";
import "./index.css";
import "./App.css";
import { CliInfo } from "./components/CliInfo";
import { NoteEditor } from "./components/Editor";
import { SearchBar } from "./components/SearchBar";
import { SidebarActions } from "./components/SideBarActions";
import { TreeView } from "./components/TreeView";
import { useTree } from "./hooks/useTree";
import type { FileNode } from "./types";

function NotesLayout() {
	const { data: tree } = useTree();
	const { noteId: activeNoteId } = useParams();
	const navigate = useNavigate();
	const [searchResults, setSearchResults] = useState<FileNode[] | null>(null);
	const [isSidebarOpen, setSidebarOpen] = useState(false);
	const [renamingId, setRenamingId] = useState<string | null>(null);

	const activeNote = useMemo(
		() => tree?.find((node) => node.id === activeNoteId) ?? null,
		[tree, activeNoteId],
	);

	const handleSelectNode = useCallback(
		(node: FileNode | null) => {
			if (node) {
				navigate(`/${node.id}`);
			} else {
				navigate("/");
			}

			if (window.innerWidth < 768) {
				setSidebarOpen(false);
			}
		},
		[navigate],
	);

	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth > 768) {
				setSidebarOpen(false);
			}
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	return (
		<div className="notes-app-container">
			<aside className={`notes-sidebar ${isSidebarOpen ? "open" : ""}`}>
				<div className="sidebar-header">
					<h1 className="sidebar-title">notes.adafia.io</h1>
					<button
						type="button"
						className="sidebar-close-button"
						onClick={() => setSidebarOpen(false)}
						aria-label="Close sidebar"
					>
						<X size={18} />
					</button>
					<SidebarActions
						parentId={null}
						onCreated={(node) => {
							handleSelectNode(node);
							setRenamingId(node.id ?? null);
						}}
					/>
				</div>

				<SearchBar onSearchResults={setSearchResults} />

				<div className="tree-view-container">
					{searchResults ? (
						<div className="search-results-container">
							<p className="search-results-header">Search Results</p>
							{searchResults.length > 0 ? (
								searchResults.map((node) => (
									<button
										type="button"
										key={node.id}
										onClick={() => handleSelectNode(node)}
										onKeyDown={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												handleSelectNode(node);
											}
										}}
										tabIndex={0}
										className="search-result-item"
									>
										<span className="search-result-name">{node.title}</span>
										{node.content && (
											<span className="search-result-snippet">
												{node.content.substring(0, 60)}...
											</span>
										)}
									</button>
								))
							) : (
								<p className="no-search-results">No matches found</p>
							)}
						</div>
					) : (
						<TreeView
							activeNote={activeNote}
							onSelect={handleSelectNode}
							renamingId={renamingId}
							setRenamingId={setRenamingId}
						/>
					)}
				</div>
			</aside>

			<main className="notes-main-content">
				<div className="mobile-header">
					<Button
						variant="secondary"
						className="mobile-sidebar-toggle"
						onClick={() => setSidebarOpen(true)}
						aria-label="Open sidebar"
					>
						<Menu size={20} />
					</Button>
					<span className="mobile-header-title">notes.adafia.io</span>
				</div>
				{activeNoteId ? (
					<NoteEditor activeNoteId={activeNoteId} onSelect={handleSelectNode} />
				) : (
					<CliInfo />
				)}
			</main>
		</div>
	);
}

import { AppRoutes } from "../components/AppRoutes";

function App() {
	return (
		<BrowserRouter basename="/notes">
			<ToastProvider>
				<Layout currentApp="notes" hideFooter={true}>
					<AuthGuard>
						<AppRoutes>
							<Route path="/:noteId?" element={<NotesLayout />} />
						</AppRoutes>
					</AuthGuard>
				</Layout>
			</ToastProvider>
		</BrowserRouter>
	);
}
export default App;
